import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';

// ---- Value Conversion Utilities ----
// Normalized [0,1] → threshold dB (-60 to 0 dBFS)
function normToThresholdDb(n) { return (Math.max(0, Math.min(1, n)) - 1) * 60; }
// Normalized [0,1] → compression ratio (1:1 to 20:1)
function normToRatio(n) { return 1 + Math.max(0, Math.min(1, n)) * 19; }
// Normalized [0,1] → attack time (0.1ms to 200ms, log scale)
function normToAttackMs(n) { return 0.1 * Math.pow(2000, Math.max(0, Math.min(1, n))); }
// Normalized [0,1] → release time (1ms to 2000ms, log scale)
function normToReleaseMs(n) { return 1 * Math.pow(2000, Math.max(0, Math.min(1, n))); }
// Normalized [0,1] → gain dB (±20 dB, 0.5 = 0 dB)
function normToGainDb(n) { return (Math.max(0, Math.min(1, n)) - 0.5) * 40; }

function formatMs(ms) {
  if (ms >= 1000) return (ms / 1000).toFixed(2).replace(/\.?0+$/, '') + 's';
  if (ms >= 100) return Math.round(ms) + 'ms';
  if (ms >= 10) return ms.toFixed(1) + 'ms';
  return ms.toFixed(2) + 'ms';
}

function formatRatio(r) {
  if (r >= 19.5) return '∞:1';
  if (r >= 10) return Math.round(r) + ':1';
  return r.toFixed(1) + ':1';
}

// ---- Compressor Transfer Curve Canvas ----
function CompressorCanvas({ params }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const W = canvas.width;
    const H = canvas.height;
    const ctx = canvas.getContext('2d');

    ctx.fillStyle = '#0d0d0d';
    ctx.fillRect(0, 0, W, H);

    const DB_MIN = -60, DB_MAX = 0;
    const pad = { left: 36, right: 10, top: 10, bottom: 26 };
    const plotW = W - pad.left - pad.right;
    const plotH = H - pad.top - pad.bottom;

    const dbToX = (db) => pad.left + (db - DB_MIN) / (DB_MAX - DB_MIN) * plotW;
    const dbToY = (db) => pad.top + (1 - (db - DB_MIN) / (DB_MAX - DB_MIN)) * plotH;
    const xToDb = (x) => DB_MIN + ((x - pad.left) / plotW) * (DB_MAX - DB_MIN);

    // Grid lines and labels
    const gridDbs = [-60, -48, -36, -24, -12, 0];
    gridDbs.forEach((db) => {
      const x = dbToX(db);
      const y = dbToY(db);

      ctx.strokeStyle = db === 0 ? '#383838' : '#1e1e1e';
      ctx.lineWidth = db === 0 ? 1.5 : 1;

      // Vertical grid line
      ctx.beginPath(); ctx.moveTo(x, pad.top); ctx.lineTo(x, pad.top + plotH); ctx.stroke();
      // Horizontal grid line
      ctx.beginPath(); ctx.moveTo(pad.left, y); ctx.lineTo(pad.left + plotW, y); ctx.stroke();

      // X-axis labels (input dB)
      ctx.fillStyle = db === 0 ? '#666' : '#444';
      ctx.font = '9px monospace';
      ctx.textAlign = 'center';
      ctx.fillText(db + '', x, H - 2);

      // Y-axis labels (output dB)
      if (db !== DB_MIN) {
        ctx.textAlign = 'right';
        ctx.fillText(db + '', pad.left - 2, y + 3);
      }
    });

    // Axis title
    ctx.fillStyle = '#555';
    ctx.font = '9px monospace';
    ctx.textAlign = 'left';
    ctx.fillText('IN →', pad.left + plotW - 28, H - 2);
    ctx.textAlign = 'right';
    ctx.fillText('↑ OUT', pad.left - 2, pad.top + 8);

    const threshDb = normToThresholdDb(params.threshold);
    const ratio = normToRatio(params.ratio);
    const outGainDb = normToGainDb(params.outgain);
    const softKnee = params.softknee;
    const kneeWidthDb = softKnee ? 8 : 0;

    // Transfer function: input dB → output dB
    function transferDb(inDb) {
      let outDb;
      if (inDb <= threshDb - kneeWidthDb / 2) {
        outDb = inDb;
      } else if (softKnee && inDb < threshDb + kneeWidthDb / 2) {
        const f = (inDb - (threshDb - kneeWidthDb / 2)) / kneeWidthDb;
        const compressed = threshDb + (inDb - threshDb) / ratio;
        outDb = inDb + f * (compressed - inDb);
      } else {
        outDb = threshDb + (inDb - threshDb) / ratio;
      }
      return Math.min(DB_MAX, outDb + outGainDb);
    }

    // Draw 1:1 reference line
    ctx.beginPath();
    ctx.moveTo(dbToX(DB_MIN), dbToY(DB_MIN));
    ctx.lineTo(dbToX(DB_MAX), dbToY(DB_MAX));
    ctx.strokeStyle = '#2e2e2e';
    ctx.lineWidth = 1;
    ctx.setLineDash([4, 4]);
    ctx.stroke();
    ctx.setLineDash([]);

    // Draw threshold line
    const threshX = dbToX(threshDb);
    if (threshX >= pad.left && threshX <= pad.left + plotW) {
      ctx.beginPath();
      ctx.moveTo(threshX, pad.top);
      ctx.lineTo(threshX, pad.top + plotH);
      ctx.strokeStyle = params.bypass ? '#2a2a2a' : 'rgba(240,192,64,0.35)';
      ctx.lineWidth = 1;
      ctx.setLineDash([3, 3]);
      ctx.stroke();
      ctx.setLineDash([]);
    }

    // Build compression curve path
    const pts = [];
    for (let px = 0; px <= plotW; px++) {
      const inDb = xToDb(pad.left + px);
      const outDb = transferDb(inDb);
      pts.push({ x: pad.left + px, y: dbToY(Math.max(DB_MIN, Math.min(DB_MAX, outDb))) });
    }

    // Fill area between 1:1 line and compression curve (gain reduction zone)
    if (!params.bypass && ratio > 1.01) {
      ctx.beginPath();
      // Top path: 1:1 line
      ctx.moveTo(dbToX(DB_MIN), dbToY(DB_MIN));
      ctx.lineTo(dbToX(DB_MAX), dbToY(DB_MAX));
      // Bottom path: compression curve (reverse)
      for (let px = plotW; px >= 0; px--) {
        ctx.lineTo(pts[px].x, pts[px].y);
      }
      ctx.closePath();
      ctx.fillStyle = 'rgba(240,192,64,0.06)';
      ctx.fill();
    }

    // Draw compression curve
    ctx.beginPath();
    pts.forEach(({ x, y }, i) => { if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y); });
    ctx.strokeStyle = params.bypass ? '#555' : '#f0c040';
    ctx.lineWidth = 2;
    ctx.stroke();

    // Info labels
    if (!params.bypass) {
      ctx.fillStyle = 'rgba(240,192,64,0.75)';
      ctx.font = '9px monospace';
      ctx.textAlign = 'left';
      ctx.fillText('T: ' + threshDb.toFixed(1) + ' dBFS', pad.left + 4, pad.top + 14);
      ctx.fillText('R: ' + formatRatio(ratio), pad.left + 4, pad.top + 26);
      if (outGainDb !== 0) {
        ctx.fillText('MG: ' + (outGainDb >= 0 ? '+' : '') + outGainDb.toFixed(1) + ' dB', pad.left + 4, pad.top + 38);
      }
    }
  }, [params]);

  return (
    <canvas
      ref={canvasRef}
      width={560}
      height={220}
      style={{ width: '100%', height: '220px', display: 'block', borderRadius: '4px', border: '1px solid #2a2a2a' }}
    />
  );
}

// ---- Parameter Row Component ----
function ParamRow({ label, color, value, onChange, displayValue }) {
  return (
    <div className="eq-band">
      <div className="eq-band-label" style={{ color }}>{label}</div>
      <div className="eq-band-param">
        <input
          type="range"
          min={0}
          max={1}
          step={0.001}
          value={value}
          onChange={(e) => onChange(parseFloat(e.target.value))}
          className="eq-slider"
        />
        <span className="eq-value">{displayValue}</span>
      </div>
    </div>
  );
}

// ---- localStorage Helpers ----
const COMP_LS_KEY = 'uisnapshot_comp_presets';
function loadCompPresets() {
  try { const r = localStorage.getItem(COMP_LS_KEY); return r ? JSON.parse(r) : []; } catch { return []; }
}
function persistCompPresets(p) {
  try { localStorage.setItem(COMP_LS_KEY, JSON.stringify(p)); } catch { /* ignore */ }
}

// ---- Main Compressor Dialog Component ----
export default function CompressorDialog({ open, onClose, read, write, channelName }) {
  const pf = (key, fb) => { const v = parseFloat(read(key)); return isNaN(v) ? fb : v; };

  const [bypass, setBypass] = useState(false);
  const [threshold, setThreshold] = useState(0.875);
  const [ratio, setRatio] = useState(0);
  const [attack, setAttack] = useState(0.158);
  const [release, setRelease] = useState(0.650);
  const [gain, setGain] = useState(0.75);
  const [outgain, setOutgain] = useState(0.333);
  const [softknee, setSoftknee] = useState(false);
  const [prname, setPrname] = useState('');

  const [presets, setPresets] = useState(loadCompPresets);
  const [selectedPreset, setSelectedPreset] = useState('');

  // Load values from snapshot when dialog opens
  useEffect(() => {
    if (!open) return;
    setBypass(String(read('dyn.bypass')) === '1');
    setThreshold(pf('dyn.threshold', 0.875));
    setRatio(pf('dyn.ratio', 0));
    setAttack(pf('dyn.attack', 0.158));
    setRelease(pf('dyn.release', 0.650));
    setGain(pf('dyn.gain', 0.75));
    setOutgain(pf('dyn.outgain', 0.333));
    setSoftknee(String(read('dyn.softknee')) === '1');
    const name = read('dyn.prname') || '';
    setPrname(name);
    setSelectedPreset(name);
  }, [open]); // intentionally only re-run when dialog opens/closes

  // Clear preset name when a parameter is changed manually
  const writeParam = useCallback((key, value) => {
    write(key, value);
    write('dyn.prname', '');
    write('dyn.prmod', 1);
    setPrname('');
    setSelectedPreset('');
  }, [write]);

  const compParams = { bypass, threshold, ratio, attack, release, gain, outgain, softknee };

  // ---- Preset helpers ----
  function applyPresetParams(p, presetName) {
    const parseF = (key, fb) => { const v = parseFloat(p[key]); return isNaN(v) ? fb : v; };

    const newBypass = String(p['dyn.bypass']) === '1';
    setBypass(newBypass); write('dyn.bypass', newBypass ? 1 : 0);

    const t = parseF('dyn.threshold', 0.875);
    setThreshold(t); write('dyn.threshold', t);

    const r = parseF('dyn.ratio', 0);
    setRatio(r); write('dyn.ratio', r);

    const a = parseF('dyn.attack', 0.158);
    setAttack(a); write('dyn.attack', a);

    const rel = parseF('dyn.release', 0.650);
    setRelease(rel); write('dyn.release', rel);

    const g = parseF('dyn.gain', 0.75);
    setGain(g); write('dyn.gain', g);

    const og = parseF('dyn.outgain', 0.333);
    setOutgain(og); write('dyn.outgain', og);

    const sk = String(p['dyn.softknee']) === '1';
    setSoftknee(sk); write('dyn.softknee', sk ? 1 : 0);

    write('dyn.prname', presetName);
    write('dyn.prmod', 0);
    setPrname(presetName);
    setSelectedPreset(presetName);
  }

  function handlePresetSelect(name) {
    setSelectedPreset(name);
    if (!name) return;
    const preset = presets.find((p) => p.name === name);
    if (preset) applyPresetParams(preset.params, name);
  }

  function handleSavePreset() {
    const name = window.prompt('Enter preset name:', prname || '');
    if (!name) return;
    const preset = {
      name,
      params: {
        'dyn.bypass': bypass ? '1' : '0',
        'dyn.threshold': String(threshold),
        'dyn.ratio': String(ratio),
        'dyn.attack': String(attack),
        'dyn.release': String(release),
        'dyn.gain': String(gain),
        'dyn.outgain': String(outgain),
        'dyn.softknee': softknee ? '1' : '0',
      },
    };
    const updated = [...presets.filter((p) => p.name !== name), preset];
    setPresets(updated);
    persistCompPresets(updated);
    write('dyn.prname', name);
    write('dyn.prmod', 0);
    setPrname(name);
    setSelectedPreset(name);
  }

  function handleExportPresets() {
    const blob = new Blob([JSON.stringify(presets, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'comp-presets.json'; a.click();
    URL.revokeObjectURL(url);
  }

  function handleImportPresets() {
    const input = document.createElement('input');
    input.type = 'file'; input.accept = '.json';
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (ev) => {
        try {
          const imported = JSON.parse(ev.target.result);
          if (!Array.isArray(imported)) throw new Error('not array');
          const merged = [...presets];
          let count = 0;
          for (const p of imported) {
            if (p && p.name && p.params) {
              const idx = merged.findIndex((x) => x.name === p.name);
              if (idx >= 0) merged[idx] = p; else merged.push(p);
              count++;
            }
          }
          setPresets(merged); persistCompPresets(merged);
          window.alert('Imported ' + count + ' preset(s).');
        } catch { window.alert('Failed to import: expected a JSON array of preset objects with name and params fields.'); }
      };
      reader.readAsText(file);
    };
    input.click();
  }

  if (!open) return null;

  const threshDb = normToThresholdDb(threshold);
  const ratioVal = normToRatio(ratio);
  const attackMs = normToAttackMs(attack);
  const releaseMs = normToReleaseMs(release);
  const gainDb = normToGainDb(gain);
  const outGainDb = normToGainDb(outgain);

  const PARAM_COLORS = ['#f0c040', '#7ec8e3', '#a0d8a0', '#e8a0e8', '#f0a070', '#f0c040'];

  return ReactDOM.createPortal(
    <div className="eq-overlay" onClick={onClose}>
      <div className="eq-dialog comp-dialog" onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div className="eq-header">
          <span className="eq-title">COMP — {channelName || 'Channel'}</span>
          <div className="eq-header-right">
            <label className="eq-toggle">
              <input
                type="checkbox" checked={bypass}
                onChange={(e) => { const v = e.target.checked; setBypass(v); write('dyn.bypass', v ? 1 : 0); }}
              />
              <span>BYPASS</span>
            </label>
            <label className="eq-toggle">
              <input
                type="checkbox" checked={softknee}
                onChange={(e) => { const v = e.target.checked; setSoftknee(v); writeParam('dyn.softknee', v ? 1 : 0); }}
              />
              <span>SOFT KNEE</span>
            </label>
            {prname && <span className="eq-active-preset" title={'Preset: ' + prname}>⬡ {prname}</span>}
            <button className="eq-close" onClick={onClose}>✕</button>
          </div>
        </div>

        {/* Transfer curve canvas */}
        <div className="eq-canvas-wrap">
          <CompressorCanvas params={compParams} />
        </div>

        {/* Parameter controls */}
        <div className="comp-params">
          <ParamRow
            label="THRESHOLD"
            color={PARAM_COLORS[0]}
            value={threshold}
            onChange={(v) => { setThreshold(v); writeParam('dyn.threshold', v); }}
            displayValue={threshDb.toFixed(1) + ' dBFS'}
          />
          <ParamRow
            label="RATIO"
            color={PARAM_COLORS[1]}
            value={ratio}
            onChange={(v) => { setRatio(v); writeParam('dyn.ratio', v); }}
            displayValue={formatRatio(ratioVal)}
          />
          <ParamRow
            label="ATTACK"
            color={PARAM_COLORS[2]}
            value={attack}
            onChange={(v) => { setAttack(v); writeParam('dyn.attack', v); }}
            displayValue={formatMs(attackMs)}
          />
          <ParamRow
            label="RELEASE"
            color={PARAM_COLORS[3]}
            value={release}
            onChange={(v) => { setRelease(v); writeParam('dyn.release', v); }}
            displayValue={formatMs(releaseMs)}
          />
          <ParamRow
            label="GAIN"
            color={PARAM_COLORS[4]}
            value={gain}
            onChange={(v) => { setGain(v); writeParam('dyn.gain', v); }}
            displayValue={(gainDb >= 0 ? '+' : '') + gainDb.toFixed(1) + ' dB'}
          />
          <ParamRow
            label="OUT GAIN"
            color={PARAM_COLORS[5]}
            value={outgain}
            onChange={(v) => { setOutgain(v); writeParam('dyn.outgain', v); }}
            displayValue={(outGainDb >= 0 ? '+' : '') + outGainDb.toFixed(1) + ' dB'}
          />
        </div>

        {/* Preset bar */}
        <div className="eq-preset-bar">
          <select
            className="eq-preset-select"
            value={selectedPreset}
            onChange={(e) => handlePresetSelect(e.target.value)}
          >
            <option value="">— Select Preset —</option>
            {presets.map((p) => (
              <option key={p.name} value={p.name}>{p.name}</option>
            ))}
          </select>
          <button className="eq-btn" onClick={handleSavePreset}>SAVE</button>
          <button className="eq-btn" onClick={handleExportPresets}>EXPORT</button>
          <button className="eq-btn" onClick={handleImportPresets}>IMPORT</button>
        </div>

      </div>
    </div>,
    document.body
  );
}
